/**
 * Sole package-private assertion boundary for P0-010 world-format brands.
 * Every helper may be called only after its complete named runtime guard has
 * succeeded. Byte helpers additionally require a fresh copy owned by the
 * caller. Nothing in this module is exported from the package public index.
 */
const Uint8ArrayIntrinsic = Uint8Array;
const arrayBufferIsView = ArrayBuffer.isView;
const numberIsSafeInteger = Number.isSafeInteger;
const reflectApply = Reflect.apply;
const typedArrayPrototype = Object.getPrototypeOf(Uint8ArrayIntrinsic.prototype);
const typedArrayTagGetter = Object.getOwnPropertyDescriptor(typedArrayPrototype, Symbol.toStringTag)?.get;
const typedArrayByteLengthGetter = Object.getOwnPropertyDescriptor(typedArrayPrototype, "byteLength")?.get;
/**
 * Realm-neutral guard for the exact byte-view kind accepted by public ports.
 * Calling the intrinsic TypedArray tag getter reads the receiver's internal
 * typed-array name, so an own `Symbol.toStringTag` cannot forge this result.
 */
export function isUint8Array(value) {
    if (!arrayBufferIsView(value) || typedArrayTagGetter === undefined) {
        return false;
    }
    try {
        return reflectApply(typedArrayTagGetter, value, []) === "Uint8Array";
    }
    catch {
        return false;
    }
}
/**
 * Reads the receiver's actual TypedArray byte length without consulting a
 * shadowable instance property. A detached buffer reports zero here; callers
 * that require bytes still have to perform a guarded copy.
 */
export function readUint8ArrayInternalByteLength(value) {
    if (!isUint8Array(value) || typedArrayByteLengthGetter === undefined) {
        return null;
    }
    try {
        const byteLength = reflectApply(typedArrayByteLengthGetter, value, []);
        return numberIsSafeInteger(byteLength) && byteLength >= 0 ? byteLength : null;
    }
    catch {
        return null;
    }
}
/**
 * Copies a genuine Uint8Array only after its intrinsic length is below the
 * allocation ceiling. Copy failures (including detachment) and any intrinsic
 * length change before/after the copy fail closed.
 */
export function copyUint8ArrayWithinLimit(value, maximumByteLength) {
    if (!numberIsSafeInteger(maximumByteLength) || maximumByteLength < 0
        || !isUint8Array(value)) {
        return null;
    }
    const sourceByteLength = readUint8ArrayInternalByteLength(value);
    if (sourceByteLength === null || sourceByteLength > maximumByteLength) {
        return null;
    }
    let copy;
    try {
        copy = new Uint8ArrayIntrinsic(value);
    }
    catch {
        return null;
    }
    const sourceByteLengthAfterCopy = readUint8ArrayInternalByteLength(value);
    const copyByteLength = readUint8ArrayInternalByteLength(copy);
    if (sourceByteLengthAfterCopy !== sourceByteLength
        || copyByteLength !== sourceByteLength) {
        return null;
    }
    return copy;
}
/** Copies a genuine Uint8Array iff its intrinsic length is exactly expected. */
export function copyUint8ArrayOfExactLength(value, expectedByteLength) {
    const copy = copyUint8ArrayWithinLimit(value, expectedByteLength);
    return copy !== null
        && readUint8ArrayInternalByteLength(copy) === expectedByteLength
        ? copy
        : null;
}
export function int16AfterRangeGuard(value) {
    return value;
}
export function int32AfterRangeGuard(value) {
    return value;
}
export function uint32AfterRangeGuard(value) {
    return value;
}
export function int64AfterRangeGuard(value) {
    return value;
}
export function uint64AfterRangeGuard(value) {
    return value;
}
export function worldSeedAfterRangeGuard(value) {
    return value;
}
export function worldRevisionAfterRangeGuard(value) {
    return value;
}
export function localSandboxRevisionAfterRangeGuard(value) {
    return value;
}
export function subchunkYAfterRangeGuard(value) {
    return value;
}
export function localVoxelAxisAfterRangeGuard(value) {
    return value;
}
export function voxelIndexAfterRangeGuard(value) {
    return value;
}
export function blockIdAfterCatalogGuard(value) {
    return value;
}
export function placeableBlockIdAfterCatalogGuard(value) {
    return value;
}
export function uuidBytesAfterLengthGuard(value) {
    return value;
}
export function sharedWorldUuidAfterLengthGuard(value) {
    return value;
}
export function localSandboxUuidAfterLengthGuard(value) {
    return value;
}
export function sha256DigestAfterLengthGuard(value) {
    return value;
}
export function sha256Prefix24AfterLengthGuard(value) {
    return value;
}
export function modificationMaskAfterLengthGuard(value) {
    return value;
}
export function biomeConfigurationAfterValueGuard(value) {
    return value;
}
export function blockCatalogBytesAfterCanonicalEncoding(value) {
    return value;
}
